/**
 * Progressively enhances native <select> elements with the BC custom dropdown UI.
 * Keeps the native select in the DOM (visually hidden) so forms, Shopify JS,
 * variant selectors, and facet filters continue to work via change/input events.
 */
(function () {
  var CARET_SVG =
    '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-caret" viewBox="0 0 10 6">' +
    '<path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"/>' +
    '</svg>';

  var openInstances = [];

  function closestNoscript(el) {
    var node = el;
    while (node) {
      if (node.nodeName === 'NOSCRIPT') return true;
      node = node.parentNode;
    }
    return false;
  }

  function shouldSkip(select) {
    if (!select || select.tagName !== 'SELECT') return true;
    if (select.dataset.customSelectEnhanced === 'true') return true;
    if (select.dataset.noCustomSelect === 'true' || select.hasAttribute('data-no-custom-select')) return true;
    if (select.multiple || select.size > 1) return true;
    if (closestNoscript(select)) return true;
    if (select.closest('[data-contact-dropdown]')) return true;
    return false;
  }

  function optionLabel(option) {
    return (option && (option.label || option.textContent) || '').trim();
  }

  function closeAll(except) {
    openInstances.slice().forEach(function (instance) {
      if (except && instance === except) return;
      instance.close();
    });
  }

  function enhanceSelect(select) {
    if (shouldSkip(select)) return null;

    select.dataset.customSelectEnhanced = 'true';

    var wrapper = document.createElement('div');
    wrapper.className = 'custom-select';
    wrapper.setAttribute('data-custom-select', '');

    if (
      select.classList.contains('caption-large') ||
      select.classList.contains('facet-filters__sort') ||
      select.closest('.facet-filters') ||
      select.closest('.mobile-facets__sort')
    ) {
      wrapper.classList.add('custom-select--compact');
    }

    var parentSelect = select.parentElement;
    if (parentSelect && parentSelect.classList.contains('select')) {
      parentSelect.classList.add('select--has-custom');
    }

    select.parentNode.insertBefore(wrapper, select);
    wrapper.appendChild(select);
    select.classList.add('custom-select__native');

    var id = select.id || 'custom-select-' + Math.random().toString(36).slice(2, 9);
    if (!select.id) select.id = id;

    var trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'custom-select__trigger';
    trigger.id = id + '-trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    trigger.setAttribute('aria-controls', id + '-list');
    if (select.disabled) trigger.disabled = true;

    var labelId = select.getAttribute('aria-labelledby');
    if (labelId) trigger.setAttribute('aria-labelledby', labelId);
    var describedBy = select.getAttribute('aria-describedby');
    if (describedBy) trigger.setAttribute('aria-describedby', describedBy);

    var valueEl = document.createElement('span');
    valueEl.className = 'custom-select__value';
    valueEl.setAttribute('data-dropdown-label', '');
    trigger.appendChild(valueEl);
    trigger.insertAdjacentHTML('beforeend', CARET_SVG);

    var menu = document.createElement('ul');
    menu.className = 'custom-select__menu';
    menu.id = id + '-list';
    menu.setAttribute('role', 'listbox');
    menu.hidden = true;

    wrapper.appendChild(trigger);
    wrapper.appendChild(menu);

    var activeIndex = -1;
    var syncingFromNative = false;
    var instance = { close: close, open: open, rebuild: rebuildOptions, select: select };

    function focusOption(index) {
      var options = menu.querySelectorAll('[role="option"]');
      options.forEach(function (option, i) {
        option.classList.toggle('is-active', i === index);
      });
      if (options[index]) options[index].focus();
    }

    function setFromOption(optionEl, closeMenu) {
      if (!optionEl || optionEl.getAttribute('aria-disabled') === 'true') return;
      var value = optionEl.getAttribute('data-value');
      var label = optionEl.textContent.trim();

      syncingFromNative = true;
      select.value = value;
      syncingFromNative = false;

      valueEl.textContent = label;
      trigger.classList.toggle('has-value', Boolean(value) || (select.selectedIndex > 0));
      menu.querySelectorAll('[role="option"]').forEach(function (opt) {
        opt.setAttribute('aria-selected', opt === optionEl ? 'true' : 'false');
      });

      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));

      if (closeMenu) {
        close();
        trigger.focus();
      }
    }

    function syncFromNative() {
      if (syncingFromNative) return;
      var selected = select.options[select.selectedIndex];
      var label = selected ? optionLabel(selected) : '';
      var value = selected ? selected.value : '';
      valueEl.textContent = label || '—';
      trigger.classList.toggle('has-value', Boolean(value) || select.selectedIndex > 0);
      trigger.disabled = select.disabled;
      menu.querySelectorAll('[role="option"]').forEach(function (opt) {
        opt.setAttribute('aria-selected', opt.getAttribute('data-value') === value ? 'true' : 'false');
      });
    }

    function rebuildOptions() {
      menu.innerHTML = '';
      Array.prototype.forEach.call(select.options, function (opt) {
        var li = document.createElement('li');
        li.setAttribute('role', 'option');
        li.tabIndex = -1;
        li.setAttribute('data-value', opt.value);
        li.setAttribute('aria-selected', opt.selected ? 'true' : 'false');
        if (opt.disabled) li.setAttribute('aria-disabled', 'true');
        li.textContent = optionLabel(opt);
        menu.appendChild(li);

        li.addEventListener('click', function () {
          setFromOption(li, true);
        });

        li.addEventListener('keydown', function (event) {
          var options = Array.prototype.slice.call(menu.querySelectorAll('[role="option"]'));
          var index = options.indexOf(li);
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            activeIndex = Math.min(index + 1, options.length - 1);
            focusOption(activeIndex);
          } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            activeIndex = Math.max(index - 1, 0);
            focusOption(activeIndex);
          } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setFromOption(li, true);
          } else if (event.key === 'Escape') {
            event.preventDefault();
            close();
            trigger.focus();
          } else if (event.key === 'Home') {
            event.preventDefault();
            activeIndex = 0;
            focusOption(activeIndex);
          } else if (event.key === 'End') {
            event.preventDefault();
            activeIndex = options.length - 1;
            focusOption(activeIndex);
          }
        });
      });
      syncFromNative();
    }

    function open() {
      if (select.disabled) return;
      closeAll(instance);
      menu.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      wrapper.classList.add('is-open');
      if (parentSelect) parentSelect.classList.add('is-open');
      if (openInstances.indexOf(instance) === -1) openInstances.push(instance);

      var options = Array.prototype.slice.call(menu.querySelectorAll('[role="option"]'));
      activeIndex = options.findIndex(function (option) {
        return option.getAttribute('aria-selected') === 'true';
      });
      if (activeIndex < 0) activeIndex = 0;
      focusOption(activeIndex);
    }

    function close() {
      menu.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      wrapper.classList.remove('is-open');
      if (parentSelect) parentSelect.classList.remove('is-open');
      activeIndex = -1;
      openInstances = openInstances.filter(function (item) {
        return item !== instance;
      });
    }

    rebuildOptions();

    select.addEventListener('focus', function () {
      trigger.focus();
    });

    trigger.addEventListener('click', function () {
      if (menu.hidden) open();
      else close();
    });

    trigger.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        open();
      }
    });

    select.addEventListener('change', syncFromNative);

    if (typeof MutationObserver !== 'undefined') {
      var optionsObserver = new MutationObserver(function () {
        rebuildOptions();
      });
      // Province/country scripts replace <option> nodes — watch childList only
      optionsObserver.observe(select, { childList: true });
    }

    return instance;
  }

  function enhanceAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var selects = scope.querySelectorAll('select');
    selects.forEach(enhanceSelect);
  }

  function onDocumentClick(event) {
    if (event.target.closest('[data-custom-select], [data-contact-dropdown]')) return;
    closeAll();
  }

  function boot() {
    enhanceAll(document);
    document.addEventListener('click', onDocumentClick);

    document.addEventListener('shopify:section:load', function (event) {
      enhanceAll(event.target);
    });

    if (typeof MutationObserver !== 'undefined') {
      var pending = [];
      var scheduled = false;

      var mo = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          mutation.addedNodes.forEach(function (node) {
            if (node.nodeType === 1) pending.push(node);
          });
        });
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(function () {
          scheduled = false;
          var nodes = pending;
          pending = [];
          nodes.forEach(function (node) {
            if (!node.isConnected) return;
            if (node.tagName === 'SELECT') {
              enhanceSelect(node);
            } else if (node.querySelectorAll) {
              enhanceAll(node);
            }
          });
        });
      });
      mo.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.BCEnhanceCustomSelects = enhanceAll;
  window.BCEnhanceSelect = enhanceSelect;
})();
